"use client";

import { useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { useAuth } from "@/context/auth-context";
import { SplashScreen } from "@/components/splash-screen";
import { PlayerProvider } from "@/context/player-context";

export default function RootWrapper({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const { user, token, loading: authLoading } = useAuth();
  const [checkingSetup, setCheckingSetup] = useState(true);
  const [showSplash, setShowSplash] = useState(true);

  // Automatically hide splash screen on auth/setup pages
  useEffect(() => {
    if (pathname === '/setup' || pathname === '/login') {
      setShowSplash(false);
    }
  }, [pathname]);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await fetch(`/api/setup/status`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        const data = await res.json();
        
        if (data.setupRequired) {
          if (pathname !== '/setup') router.push('/setup');
          setCheckingSetup(false);
          return;
        }

        // If setup is NOT required, we shouldn't be on /setup
        if (pathname === '/setup') {
          if (token) {
            router.push('/');
          } else {
            router.push('/login');
          }
          setCheckingSetup(false);
          return;
        }

        if (!token) {
          if (pathname !== '/login') {
            router.push('/login');
          }
        } else {
          if (pathname === '/login') {
            router.push('/');
          }
        }

        setCheckingSetup(false);
      } catch (err) {
        console.error("Failed to check status", err);
        if (!token && pathname !== '/setup' && pathname !== '/login') {
          router.push('/setup');
        }
        setCheckingSetup(false);
      }
    };

    if (!authLoading) {
      checkStatus();
    }
  }, [pathname, router, token, authLoading]);

  // Show splash only on initial app load, not on every internal navigation
  if (showSplash) {
    return <SplashScreen onComplete={() => setShowSplash(false)} />;
  }

  if (authLoading || checkingSetup) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black">
        <div className="relative">
          <div className="h-12 w-12 animate-spin rounded-full border-4 border-white/10 border-t-white" />
        </div>
      </div>
    );
  }

  return (
    <PlayerProvider>
      {children}
    </PlayerProvider>
  );
}
